import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { processSteps } from '@/mocks/homepage';

export default function Process() {
  const { t } = useTranslation('homepage');
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollReveal<HTMLDivElement>(0.1);

  return (
    <section id="process" className="bg-white py-20 md:py-28 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        {/* Title */}
        <div
          ref={titleRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl">
            {t('process_h2')}
          </h2>
        </div>

        {/* Image + Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: Image */}
          <div
            className={`lg:col-span-4 transition-all duration-1000 delay-100 ${
              stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="rounded-lg overflow-hidden sticky top-24">
              <img
                src="https://readdy.ai/api/search-image?query=A%20close-up%20photograph%20of%20a%20skilled%20roofer%20wearing%20a%20bright%20orange%20hard%20hat%20and%20high-visibility%20reflective%20vest%2C%20actively%20nailing%20dark%20asphalt%20shingles%20on%20a%20steep%20residential%20roof%2C%20power%20tool%20in%20hand%2C%20rows%20of%20freshly%20laid%20black%20shingles%20gleaming%20under%20bright%20midday%20sunlight%2C%20blurred%20green%20trees%20and%20suburban%20houses%20in%20the%20background%2C%20vivid%20blue%20sky%20with%20white%20clouds%2C%20sharp%20documentary%20photography%20capturing%20craftsmanship%20and%20motion%20of%20roof%20installation%20work%2C%20no%20text%20or%20branding%20visible&width=500&height=700&seq=home-roofer-action-v2&orientation=portrait"
                alt="Inspection gratuite de toiture à Lévis par un couvreur professionnel certifié RBQ"
                className="w-full h-80 lg:h-[520px] object-cover object-top"
                loading="lazy"
                width="500"
                height="700"
              />
              <div className="bg-slate-primary p-4">
                <p className="text-white/90 text-sm font-medium">
                  <i className="ri-search-line mr-2 text-copper" />
                  Inspection gratuite sous 48h
                </p>
              </div>
            </div>
          </div>

          {/* Right: Timeline */}
          <div
            ref={stepsRef}
            className={`lg:col-span-8 transition-all duration-1000 delay-200 ${
              stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Desktop: horizontal timeline */}
            <div className="hidden md:block">
              <div className="flex items-start justify-between relative">
                {/* Connecting line */}
                <div className="absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-soft-dark" />

                {processSteps.map((step, index) => (
                  <div
                    key={step.step}
                    className="relative flex flex-col items-center text-center z-10"
                    style={{ width: '22%' }}
                  >
                    {/* Circle */}
                    <div className="w-16 h-16 rounded-full bg-slate-primary flex items-center justify-center mb-5 shadow-md">
                      <i className={`${step.icon} text-white text-xl`} />
                    </div>
                    {/* Number badge */}
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-copper text-white text-xs font-bold flex items-center justify-center">
                      {step.step}
                    </div>
                    <h3 className="font-manrope font-bold text-slate-primary text-base lg:text-lg mb-2">
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {t(step.descKey)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile: vertical timeline */}
            <div className="md:hidden space-y-8">
              {processSteps.map((step) => (
                <div key={step.step} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-slate-primary flex items-center justify-center shrink-0">
                      <i className={`${step.icon} text-white text-lg`} />
                    </div>
                    {step.step < 4 && (
                      <div className="w-0.5 h-12 bg-soft-dark mt-2" />
                    )}
                  </div>
                  <div className="pt-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 rounded-full bg-copper text-white text-xs font-bold flex items-center justify-center">
                        {step.step}
                      </span>
                      <h3 className="font-manrope font-bold text-slate-primary text-base">
                        {t(step.titleKey)}
                      </h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {t(step.descKey)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}